import React from 'react';
import { motion } from 'framer-motion';

const STATUSES = [
  { key: 'open', label: 'Aperta', color: '#00CED1', description: 'In attesa di proposte' },
  { key: 'confirmed', label: 'Confermato', color: '#3B82F6', description: 'Ordine confermato' },
  { key: 'in_progress', label: 'In Lavorazione', color: '#DAA520', description: 'Artigiano al lavoro' },
  { key: 'ready', label: 'Pronto', color: '#8B5CF6', description: 'Pronto per la spedizione' },
  { key: 'shipped', label: 'Spedito', color: '#6366F1', description: 'In viaggio verso di te' },
  { key: 'delivered', label: 'Consegnato', color: '#10B981', description: 'Consegnato con successo' },
  { key: 'completed', label: 'Completato', color: '#008080', description: 'Ordine completato' },
  { key: 'cancelled', label: 'Annullato', color: '#EF4444', description: 'Ordine annullato' },
];

const getStatusIndex = (status) => {
  const index = STATUSES.findIndex(s => s.key === status);
  return index >= 0 ? index : 0;
};

const getStatusConfig = (status) => {
  return STATUSES.find(s => s.key === status) || STATUSES[0];
};

// Animated faceted crystal SVG
const CrystalIcon = ({ color, isActive, size = 80 }) => (
  <motion.svg
    width={size}
    height={size}
    viewBox="0 0 100 100"
    className="drop-shadow-lg"
    animate={isActive ? {
      scale: [1, 1.05, 1],
      rotate: [0, 5, -5, 0],
    } : {}}
    transition={{
      duration: 2,
      repeat: Infinity,
      ease: "easeInOut"
    }}
  >
    {/* Main crystal body */}
    <motion.polygon
      points="50,5 85,35 75,90 25,90 15,35"
      fill={color}
      stroke={color}
      strokeWidth="2"
      animate={isActive ? {
        opacity: [0.9, 1, 0.9],
      } : { opacity: 0.9 }}
      transition={{
        duration: 1.5,
        repeat: Infinity,
      }}
    />
    
    {/* Left facet */}
    <polygon
      points="50,5 15,35 25,90 50,70"
      fill={color}
      opacity="0.7"
    />
    
    {/* Right facet */}
    <polygon
      points="50,5 85,35 75,90 50,70"
      fill={color}
      opacity="0.85"
    />
    
    {/* Top highlight */}
    <polygon
      points="50,5 65,25 50,40 35,25"
      fill="white"
      opacity="0.3"
    />
    
    {/* Inner shine */}
    <motion.ellipse
      cx="50"
      cy="45"
      rx="12"
      ry="8"
      fill="white"
      initial={{ opacity: 0.2, rx: 12, ry: 8 }}
      animate={isActive ? {
        opacity: [0.2, 0.4, 0.2],
        rx: [12, 14, 12],
        ry: 8,
      } : { opacity: 0.2, rx: 12, ry: 8 }}
      transition={{
        duration: 2,
        repeat: Infinity,
      }}
    />
  </motion.svg>
);

// Mini crystal for timeline
const MiniCrystal = ({ color, filled, index }) => (
  <motion.div
    initial={{ scale: 0 }}
    animate={{ scale: 1 }}
    transition={{ delay: index * 0.1 }}
    className="relative"
  >
    <svg width="24" height="24" viewBox="0 0 100 100">
      <polygon
        points="50,10 80,35 70,85 30,85 20,35"
        fill={filled ? color : 'transparent'}
        stroke={color}
        strokeWidth="4"
      />
    </svg>
  </motion.div>
);

export const CrystalTracker = ({ status, showTimeline = true, size = 'large' }) => {
  const currentStatus = getStatusConfig(status);
  const currentIndex = getStatusIndex(status);
  const isCancelled = status === 'cancelled';
  
  const crystalSize = size === 'large' ? 100 : size === 'medium' ? 70 : 50;
  
  return (
    <div className="flex flex-col items-center" data-testid="crystal-tracker">
      {/* Main Crystal */}
      <div className="relative mb-4">
        <CrystalIcon 
          color={currentStatus.color} 
          isActive={!isCancelled && status !== 'completed'}
          size={crystalSize}
        />
        
        {/* Glow effect */}
        {!isCancelled && (
          <motion.div
            className="absolute inset-0 rounded-full"
            style={{
              background: `radial-gradient(circle, ${currentStatus.color}40 0%, transparent 70%)`,
            }}
            animate={{
              scale: [1, 1.3, 1],
              opacity: [0.5, 0.2, 0.5],
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
            }}
          />
        )}
      </div>
      
      {/* Status Label */}
      <motion.div
        key={status}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center"
      >
        <p 
          className="font-bold text-lg"
          style={{ color: currentStatus.color }}
        >
          {currentStatus.label}
        </p>
        <p className="text-sm text-slate-500">{currentStatus.description}</p>
      </motion.div>
      
      {/* Timeline */}
      {showTimeline && !isCancelled && (
        <div className="mt-6 flex items-center gap-1">
          {STATUSES.filter(s => !['open', 'cancelled'].includes(s.key)).map((s, index) => (
            <React.Fragment key={s.key}>
              <MiniCrystal
                color={index <= currentIndex - 1 ? s.color : '#CBD5E1'}
                filled={index <= currentIndex - 1}
                index={index}
              />
              {index < 5 && (
                <motion.div
                  className="w-4 h-0.5 rounded-full"
                  style={{
                    backgroundColor: index < currentIndex - 1 ? STATUSES[index + 2]?.color : '#CBD5E1'
                  }}
                  initial={{ scaleX: 0 }}
                  animate={{ scaleX: 1 }}
                  transition={{ delay: index * 0.15 }}
                />
              )}
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  );
};

// Compact version for cards
export const CrystalTrackerCompact = ({ status }) => {
  const currentStatus = getStatusConfig(status);
  
  return (
    <div className="flex items-center gap-3" data-testid="crystal-tracker-compact">
      <CrystalIcon 
        color={currentStatus.color} 
        isActive={status !== 'completed' && status !== 'cancelled'}
        size={40}
      />
      <div>
        <p 
          className="font-semibold text-sm"
          style={{ color: currentStatus.color }}
        >
          {currentStatus.label}
        </p>
        <p className="text-xs text-slate-500">{currentStatus.description}</p>
      </div>
    </div>
  );
};

export default CrystalTracker;
